'use strict';

var addDays = require('date-fns/addDays');
var areIntervalsOverlapping = require('date-fns/areIntervalsOverlapping');
var differenceInCalendarDays = require('date-fns/differenceInCalendarDays');
var endOfMonth = require('date-fns/endOfMonth');
var endOfWeek = require('date-fns/endOfWeek');
var isBefore = require('date-fns/isBefore');
var isSameDay = require('date-fns/isSameDay');
var parse = require('date-fns/parse');
var startOfMonth = require('date-fns/startOfMonth');
var startOfWeek = require('date-fns/startOfWeek');
var React = require('react');
var reactUtils = require('@sravni/react-utils');
var index = require('../../DateInput/constants/index.js');
var Space = require('../../Space/src/Space.js');
var Calendar_module = require('../styles/Calendar.module.less.js');
var Week = require('./Week.js');

function _interopNamespaceDefault(e) {
    var n = Object.create(null);
    if (e) {
        Object.keys(e).forEach(function (k) {
            if (k !== 'default') {
                var d = Object.getOwnPropertyDescriptor(e, k);
                Object.defineProperty(n, k, d.get ? d : {
                    enumerable: true,
                    get: function () { return e[k]; }
                });
            }
        });
    }
    n.default = e;
    return Object.freeze(n);
}

var React__namespace = /*#__PURE__*/_interopNamespaceDefault(React);

const Month = (props) => {
    const { month, minDate, maxDate, rangeLimit, disabledIntervals, selectionStart, selectionEnd, selectionInProgress, mode, className, onChange, ...restProps } = props;
    const [highlightedStart, setHighlightedStart] = React__namespace.useState(undefined);
    const [highlightedEnd, setHighlightedEnd] = React__namespace.useState(undefined);
    const cx = reactUtils.useStyles(Calendar_module.default);
    const classNames = cx(className, 'month');
    const renderHeader = React__namespace.useMemo(() => (React__namespace.createElement(Space.Space, { size: 0, align: "center", justify: "center", className: cx('days-of-week') }, index.DAYS_OF_WEEK.map((d) => (React__namespace.createElement(Space.Space, { key: d, size: 0, align: "center", justify: "center", className: cx('day-of-week') }, d))))
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [cx]);
    const getMinDate = React__namespace.useCallback(() => selectionStart || minDate, [minDate, selectionStart]);
    const getMaxDate = React__namespace.useCallback(() => (selectionStart && rangeLimit ? addDays.addDays(selectionStart, rangeLimit) : maxDate), [maxDate, selectionStart, rangeLimit]);
    const getDisabledRange = React__namespace.useCallback((interval) => {
        const { from, to } = interval;
        if (!disabledIntervals)
            return true;
        for (let i = 0; i < disabledIntervals.length; i++) {
            const { from: intervalStart, to: intervalEnd } = disabledIntervals[i];
            if (areIntervalsOverlapping.areIntervalsOverlapping({
                start: from,
                end: to,
            }, {
                start: intervalStart,
                end: intervalEnd,
            })) {
                return;
            }
        }
        return true;
    }, [disabledIntervals]);
    const pushUpdate = React__namespace.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-shadow
    (selectionStart, selectionEnd) => {
        if (!selectionStart || !selectionEnd || typeof onChange !== 'function')
            return;
        const from = isBefore.isBefore(selectionStart, selectionEnd) ? selectionStart : selectionEnd;
        const to = isBefore.isBefore(selectionStart, selectionEnd) ? selectionEnd : selectionStart;
        const toWithLimits = rangeLimit && rangeLimit < differenceInCalendarDays.differenceInCalendarDays(from, to) ? addDays.addDays(from, rangeLimit) : undefined;
        onChange({
            from,
            to: toWithLimits ?? to,
        });
    }, [rangeLimit, onChange]);
    const handleChangeSelected = React__namespace.useCallback((date) => {
        let from = selectionStart;
        let to = selectionEnd;
        if (mode === 'range') {
            if (selectionInProgress && selectionStart) {
                const isDisabledWithin = getDisabledRange({
                    from: !isBefore.isBefore(selectionStart, date) ? selectionStart : date,
                    to: isBefore.isBefore(selectionStart, date) ? selectionStart : date,
                });
                if (!isDisabledWithin) {
                    from = undefined;
                    to = undefined;
                    pushUpdate(from, to);
                    return;
                }
                to = date;
            }
            else {
                from = date;
                to = date;
            }
            pushUpdate(from, to);
        }
        else {
            from = date;
            to = date;
            pushUpdate(from, to);
        }
    }, [mode, selectionStart, selectionInProgress, selectionEnd, pushUpdate, getDisabledRange]);
    const handleOnDayMouseEnter = React__namespace.useCallback((event) => {
        if (!selectionInProgress || !selectionStart)
            return;
        const { currentTarget: { dataset: { date: day }, }, } = event;
        const date = parse.parse(String(day), 'yyyy-MM-dd', new Date());
        const isDisabledWithin = getDisabledRange({
            from: !isBefore.isBefore(selectionStart, date) ? selectionStart : date,
            to: isBefore.isBefore(selectionStart, date) ? selectionStart : date,
        });
        if (!isDisabledWithin)
            return;
        if (date < selectionStart) {
            setHighlightedStart(date);
            setHighlightedEnd(selectionStart);
        }
        else {
            setHighlightedStart(selectionStart);
            setHighlightedEnd(date);
        }
    }, [selectionStart, selectionInProgress, getDisabledRange]);
    const renderWeeks = React__namespace.useMemo(() => {
        let innerMinDate = minDate;
        let innerMaxDate = maxDate;
        let start = startOfWeek.startOfWeek(startOfMonth.startOfMonth(month), { weekStartsOn: 1 });
        const end = endOfWeek.endOfWeek(endOfMonth.endOfMonth(month), { weekStartsOn: 1 });
        const weeks = [];
        if (selectionInProgress && rangeLimit) {
            innerMinDate = getMinDate();
            innerMaxDate = getMaxDate();
        }
        while (isBefore.isBefore(start, end) || isSameDay.isSameDay(start, end)) {
            weeks.push(start);
            start = addDays.addDays(start, 7);
        }
        return weeks.map((week) => (React__namespace.createElement(Week.Week, { key: week.getTime(), date: week, month: month, minDate: innerMinDate, maxDate: innerMaxDate, disabledIntervals: disabledIntervals, mode: mode, selectionStart: selectionStart, selectionEnd: selectionEnd, highlightedStart: selectionInProgress ? highlightedStart : undefined, highlightedEnd: selectionInProgress ? highlightedEnd : undefined, onChange: handleChangeSelected, onMouseEnter: handleOnDayMouseEnter })));
    }, [
        month,
        selectionStart,
        selectionEnd,
        selectionInProgress,
        highlightedStart,
        highlightedEnd,
        rangeLimit,
        minDate,
        maxDate,
        disabledIntervals,
        mode,
        handleChangeSelected,
        handleOnDayMouseEnter,
        getMaxDate,
        getMinDate,
    ]);
    React__namespace.useEffect(() => {
        setHighlightedStart(undefined);
        setHighlightedEnd(undefined);
    }, [selectionStart]);
    return (React__namespace.createElement("div", { className: classNames, ...restProps },
        renderHeader,
        renderWeeks));
};
Month.displayName = 'Month';

exports.Month = Month;
//# sourceMappingURL=Month.js.map
